import { FormControlLabel, Tooltip } from '@mui/material';
import { FormikProps } from 'formik';
import * as React from 'react';

import { FormikSwitch } from '../../../common/formik-material-ui';
import {
  trackToggledIgnoreDNC,
  trackToggledPowerListActive,
  trackToggledPreventDeselection,
  trackToggledRequireCallOutcome,
} from '../../../common/userTracking';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';

const ConditionalWrapper = ({
  condition,
  wrapper,
  children,
}: {
  condition: boolean;
  wrapper: (children: React.ReactElement) => React.ReactElement;
  children: React.ReactElement;
}) => (condition ? wrapper(children) : children);
export function Advanced(props: {
  formik: FormikProps<Record<string, unknown>>;
  isMappingCRM: boolean;
  disablePowerlistToggle: boolean;
  isAgentPowerlist: number | undefined;
  id: number | null;
}) {
  const { isMappingCRM } = props;

  const { treatment } = useFeatureFlag('SWARM-40-prevent-auto-start-on-logon');
  const isPreventDeselectionEnabled = React.useMemo(
    () => treatment === 'control' || treatment === 'on',
    [treatment]
  );

  return (
    <div className="container">
      {!isMappingCRM && (
        <>
          <div className="row borderBottom">
            <div className="col-lg-8">
              <h5 className="fieldTitle">Active</h5>
              <p className="fieldDescription">
                Toggle on or off to set the PowerList to active or inactive.
                Setting a list to inactive is useful if it is intended for
                seasonal or temporary use and will be enabled at another time.
              </p>
            </div>
            <div className="col-lg-4">
              <ConditionalWrapper
                condition={
                  props.disablePowerlistToggle && props.isAgentPowerlist === 1
                }
                wrapper={(children: React.ReactElement) => (
                  <Tooltip title="Agent powerlists are disabled">
                    {children}
                  </Tooltip>
                )}
              >
                <FormControlLabel
                  onClick={() => {
                    trackToggledPowerListActive();
                  }}
                  control={
                    <FormikSwitch
                      name="status"
                      formik={props.formik}
                      color="primary"
                      type="checkbox"
                      disabled={Boolean(
                        props.disablePowerlistToggle &&
                          props.isAgentPowerlist === 1
                      )}
                    />
                  }
                  label="Active"
                />
              </ConditionalWrapper>
            </div>
          </div>
          <div className="row borderBottom">
            <div className="col-lg-8">
              <h5 className="fieldTitle">Prevent Deselection</h5>
              <p className="fieldDescription">
                {isPreventDeselectionEnabled
                  ? 'When toggled ON, Agents cannot unsubscribe from this PowerList.'
                  : 'When toggled ON, the PowerList session will begin instantly when your Agents log into the PowerCall dialer.'}
              </p>
            </div>
            <div className="col-lg-4">
              <FormControlLabel
                onClick={() => {
                  trackToggledPreventDeselection();
                }}
                control={
                  <FormikSwitch
                    name="autostartOnLogon"
                    formik={props.formik}
                    color="primary"
                    type="checkbox"
                  />
                }
                label="Prevent Deselection"
              />
            </div>
          </div>
        </>
      )}
      <div className="row borderBottom">
        <div className="col-lg-8">
          <h5 className="fieldTitle">Require Call Outcome</h5>
          <p className="fieldDescription">
            When toggled ON, Agents must select a call outcome after each call
            before moving on to the next call.
          </p>
        </div>
        <div className="col-lg-4">
          <FormControlLabel
            onClick={() => {
              trackToggledRequireCallOutcome();
            }}
            control={
              <FormikSwitch
                name="mustDespositionCalls"
                formik={props.formik}
                color="primary"
                type="checkbox"
              />
            }
            label="Require Call Outcome"
          />
        </div>
      </div>
      {process.env.VITE_APP_POWERLIST_IGNORE_DNC === '1' && (
        <div className="row">
          <div className="col-lg-8">
            <h5 className="fieldTitle">Ignore DNC</h5>
            <p className="fieldDescription">
              Turning on this option will enable PowerList to ignore both the
              FTC and your business’s blocked numbers list. This option should
              only be used for contacts that have provided consent to call,
              ie…”Requested information”
            </p>
          </div>
          <div className="col-lg-4">
            <FormControlLabel
              onClick={() => {
                trackToggledIgnoreDNC();
              }}
              control={
                <FormikSwitch
                  name="ignoreDNC"
                  formik={props.formik}
                  color="primary"
                  type="checkbox"
                />
              }
              label="Ignore DNC"
            />
          </div>
        </div>
      )}
    </div>
  );
}
